import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { FaChevronLeft, FaChevronRight, FaTimes } from "react-icons/fa";
import { cloudinaryImage } from "../utils/cloudinary";

const ModalPortal = ({ selectedIndex, images, onClose, onNext, onPrev }) => {
  useEffect(() => {
    if (selectedIndex !== null) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [selectedIndex]);

  return createPortal(
    <AnimatePresence>
      {selectedIndex !== null && (
        <motion.div
          className="fixed inset-0 bg-black/80 flex flex-col items-center justify-center z-[9999]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <div 
            className="relative w-[90%] max-w-3xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={cloudinaryImage(images[selectedIndex])}
              alt="selected"
              className="w-full rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.55)] object-contain max-h-[80vh]"
            />

            <button
              onClick={onClose}
              className="absolute top-3 right-3 rounded-xl px-3 py-2
                         text-white/90 hover:text-white bg-white/10 hover:bg-white/15 transition"
            >
              <FaTimes />
            </button>

            <button
              onClick={onPrev}
              className="absolute top-1/2 left-3 -translate-y-1/2 rounded-xl px-3 py-2
                         text-white/90 hover:text-white bg-white/10 hover:bg-white/15 transition"
            >
              <FaChevronLeft />
            </button>

            <button
              onClick={onNext}
              className="absolute top-1/2 right-3 -translate-y-1/2 rounded-xl px-3 py-2
                         text-white/90 hover:text-white bg-white/10 hover:bg-white/15 transition"
            >
              <FaChevronRight />
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

const COLLAGE_PATTERN = [
  "col-span-12 md:col-span-6 lg:col-span-5 row-span-2",
  "col-span-12 md:col-span-6 lg:col-span-3 row-span-1",
  "col-span-12 md:col-span-6 lg:col-span-4 row-span-2",
  "col-span-12 md:col-span-6 lg:col-span-3 row-span-1",
  "col-span-12 md:col-span-6 lg:col-span-3 row-span-1",
  "col-span-12 md:col-span-6 lg:col-span-4 row-span-1",
  "col-span-12 md:col-span-6 lg:col-span-5 row-span-1",
  "col-span-12 md:col-span-6 lg:col-span-3 row-span-1",
  "col-span-12 md:col-span-6 lg:col-span-4 row-span-1",
];

export default function GridGallery({ images, layout = "masonry" }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  const handleNext = () =>
    setSelectedIndex((prev) => (prev + 1) % images.length);

  const handlePrev = () =>
    setSelectedIndex((prev) => (prev - 1 + images.length) % images.length);

  // Determine grid span based on index for masonry effect
  const getGridSpan = (index) => {
    const pattern = [2, 1, 1, 2, 1, 1, 2, 1, 1]; // Repeating pattern for variety
    const span = pattern[index % pattern.length];
    return span;
  };

  return (
    <>
      {layout === "collage" ? (
        <div className="bg-black p-1">
          <div className="grid grid-cols-12 grid-flow-dense gap-1 md:gap-1.5 auto-rows-[140px] md:auto-rows-[180px] lg:auto-rows-[210px]">
            {images.map((src, index) => {
              const spanClass = COLLAGE_PATTERN[index % COLLAGE_PATTERN.length];
              return (
                <motion.button
                  type="button"
                  key={`${src}-${index}`}
                  whileHover={{ scale: 1.01 }}
                  className={`relative overflow-hidden bg-black ${spanClass}`}
                  onClick={() => setSelectedIndex(index)}
                >
                  <img
                    src={cloudinaryImage(src)}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover"
                  />
                </motion.button>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          className="p-4"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
            gridAutoFlow: "dense",
            gap: "0.75rem",
          }}
        >
          {images.map((src, index) => {
            const span = getGridSpan(index);
            return (
              <motion.div
                key={`${src}-${index}`}
                whileHover={{ scale: 1.05 }}
                className="media-tile cursor-pointer rounded-lg overflow-hidden"
                onClick={() => setSelectedIndex(index)}
                style={{
                  gridColumn: span === 2 ? "span 2" : "span 1",
                  gridRow: span === 2 ? "span 2" : "span 1",
                }}
              >
                <img
                  src={cloudinaryImage(src)}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover hover:scale-[1.08] transition duration-500"
                />
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal rendered to document.body via portal */}
      <ModalPortal
        selectedIndex={selectedIndex}
        images={images}
        onClose={() => setSelectedIndex(null)}
        onNext={handleNext}
        onPrev={handlePrev}
      />
    </>
  );
}
